import { Rows, SquaresFour } from "@phosphor-icons/react";
import { Flex } from "@radix-ui/themes";
import cx from "classnames";
import { useTranslation } from "react-i18next";

import {
  TOOLBAR_ICON_SIZE,
  TOOLBAR_TOGGLE_CONTAINER,
  toolbarToggleButtonClass,
} from "../toolbarControls";

/** The two view modes every page-header toolbar toggles between. */
export type ToolbarView = "grid" | "list";

/**
 * The data-testid strings the consuming toolbar needs on the toggle. Both the
 * Projects toolbar (`campaigns.tsx`) and the Documents toolbar
 * (`TicketsHeader.tsx`) render the SAME `ToolbarViewToggle`, but their e2e specs
 * target page-scoped testids, so each page passes its own.
 */
export type ToolbarViewToggleTestIds = {
  grid: string;
  list: string;
};

type ToolbarViewToggleProps = {
  value: ToolbarView;
  onChange: (view: ToolbarView) => void;
  /** Accessible name for the toggle group, e.g. "Project view". */
  groupLabel: string;
  testIds: ToolbarViewToggleTestIds;
  /** Testid on the group container (Documents e2e asserts its visibility). */
  "data-testid"?: string;
  /** Hide the control (kept rendered for testid stability) when off-route. */
  hidden?: boolean;
};

/**
 * The single, shared grid/list view toggle used by every page-header toolbar.
 *
 * It is a hand-rolled segmented control (NOT Radix `SegmentedControl`) so its
 * height, border, radius, and active/inactive colors come from the shared
 * `toolbarControls` tokens and stay pixel-identical across pages. Buttons expose
 * BOTH `data-state` ("on"/"off") and `role="radio"` + `aria-checked` so the
 * existing deployed Documents specs (which assert each) keep passing after the
 * Radix `SegmentedControl` is swapped out for this.
 */
export function ToolbarViewToggle({
  value,
  onChange,
  groupLabel,
  testIds,
  "data-testid": dataTestId,
  hidden = false,
}: ToolbarViewToggleProps) {
  const { t } = useTranslation("components");
  return (
    <Flex
      gap="0"
      role="group"
      aria-label={groupLabel}
      data-testid={dataTestId}
      className={cx(TOOLBAR_TOGGLE_CONTAINER, { hidden })}
    >
      <button
        type="button"
        role="radio"
        data-testid={testIds.grid}
        aria-label={t("toolbarViewToggle.gridView")}
        aria-checked={value === "grid"}
        data-state={value === "grid" ? "on" : "off"}
        className={toolbarToggleButtonClass(value === "grid")}
        onClick={() => onChange("grid")}
      >
        <SquaresFour size={TOOLBAR_ICON_SIZE} aria-hidden="true" />
      </button>
      <button
        type="button"
        role="radio"
        data-testid={testIds.list}
        aria-label={t("toolbarViewToggle.listView")}
        aria-checked={value === "list"}
        data-state={value === "list" ? "on" : "off"}
        className={`border-l border-puntt-neutral-gray-6 ${toolbarToggleButtonClass(
          value === "list",
        )}`}
        onClick={() => onChange("list")}
      >
        <Rows size={TOOLBAR_ICON_SIZE} aria-hidden="true" />
      </button>
    </Flex>
  );
}
